import * as React from "react";
import { Search, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface DataTableProps<T> {
  title: string;
  description?: string;
  data: T[] | undefined;
  isLoading: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  searchPlaceholder?: string;
  onCreateNew?: () => void;
  createButtonLabel?: string;
  columns: {
    header: string;
    key?: keyof T;
    className?: string;
    render?: (item: T) => React.ReactNode;
  }[];
  onRowClick?: (item: T) => void;
  emptyIcon?: React.ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
  filterData?: (item: T, query: string) => boolean;
  actions?: React.ReactNode;
}

export function DataTable<T extends { id: number | string }>({
  title,
  description,
  data,
  isLoading,
  searchQuery = "",
  onSearchChange,
  searchPlaceholder = "Buscar...",
  onCreateNew,
  createButtonLabel = "Crear Nuevo",
  columns,
  onRowClick,
  emptyIcon,
  emptyTitle = "No hay datos todavía",
  emptyDescription = "Comienza creando un nuevo elemento.",
  filterData,
  actions,
}: DataTableProps<T>) {
  // Filtrar datos si hay búsqueda
  const filteredData = React.useMemo(() => {
    if (!data) return [];
    if (!searchQuery || !filterData) return data;
    return data.filter((item) => filterData(item, searchQuery));
  }, [data, searchQuery, filterData]);

  const showSearch = onSearchChange !== undefined;
  const showCreateButton = onCreateNew !== undefined;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{title}</h1>
            {description && (
              <p className="text-muted-foreground mt-1">{description}</p>
            )}
          </div>
          {showCreateButton && (
            <Button disabled>
              <Plus className="mr-2 h-4 w-4" />
              {createButtonLabel}
            </Button>
          )}
        </div>
        {showSearch && <Skeleton className="h-10 w-full" />}
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{title}</h1>
          <p className="text-muted-foreground mt-1">
            {description || `${filteredData?.length || 0} elemento${filteredData?.length !== 1 ? 's' : ''}`}
            {searchQuery && ` encontrado${filteredData?.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {actions}
          {showCreateButton && (
            <Button onClick={onCreateNew}>
              <Plus className="mr-2 h-4 w-4" />
              {createButtonLabel}
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      {showSearch && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      {/* Empty State - Sin datos */}
      {(!filteredData || filteredData.length === 0) && !searchQuery && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            {emptyIcon && (
              <div className="rounded-full bg-muted p-4 mb-4">
                {emptyIcon}
              </div>
            )}
            <h3 className="text-lg font-semibold mb-2">{emptyTitle}</h3>
            <p className="text-muted-foreground mb-6 max-w-sm">
              {emptyDescription}
            </p>
            {showCreateButton && (
              <Button onClick={onCreateNew}>
                <Plus className="mr-2 h-4 w-4" />
                {createButtonLabel}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty State - Sin resultados de búsqueda */}
      {searchQuery && filteredData?.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Search className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No se encontraron resultados</h3>
            <p className="text-muted-foreground mb-6 max-w-sm">
              No hay elementos que coincidan con "{searchQuery}"
            </p>
            <Button variant="outline" onClick={() => onSearchChange?.("")}>
              Limpiar búsqueda
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      {filteredData && filteredData.length > 0 && (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((column, index) => (
                  <TableHead key={index} className={column.className}>
                    {column.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.map((item) => (
                <TableRow
                  key={item.id}
                  className={onRowClick ? "cursor-pointer hover:bg-muted/50 transition-colors" : ""}
                  onClick={() => onRowClick?.(item)}
                >
                  {columns.map((column, index) => (
                    <TableCell key={index} className={column.className}>
                      {column.render
                        ? column.render(item)
                        : column.key
                        ? String(item[column.key] ?? "-")
                        : "-"}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
